import * as React from 'react';

import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cva } from 'class-variance-authority';

import { cn } from '../../../../lib/stylingUtils';

const kanbanTaskVariants = cva(
  'flex h-full max-h-[90px] w-[250px] rounded-lg border border-foreground/10 bg-space p-0.5 select-none',
  {
    variants: {
      selected: {
        true: 'ring-2 ring-primary',
      },
      dragging: {
        over: 'cursor-grabbing opacity-30 ring-2',
        overlay: 'cursor-grabbing ring-2 ring-primary',
      },
    },
  },
);

export interface Task {
  id: UniqueIdentifier;
  columnId: UniqueIdentifier;
  rank: string;
  selected?: boolean;
  content: () => React.ReactNode;
}

export interface TaskCardProps {
  task: Task;
  isOverlay?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  className?: string;
}

export type TaskType = 'Task';

export interface TaskDragData {
  type: TaskType;
  task: Task;
}

export const TaskCard = ({
  task,
  onClick,
  isOverlay,
  className,
}: TaskCardProps) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: {
      type: 'Task',
      task,
    } satisfies TaskDragData,
    attributes: {
      roleDescription: 'Task',
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        '@TaskCard',
        kanbanTaskVariants({
          selected: task.selected,
          dragging: isOverlay ? 'overlay' : isDragging ? 'over' : undefined,
          className,
        }),
      )}
      {...attributes}
      {...listeners}
      onClick={!isOverlay ? onClick : undefined}
    >
      {task.content()}
    </div>
  );
};
